import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, LoadingController } from '@ionic/angular';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
// Models
import { droneIcon, droneMoccaIcon } from 'src/assets/icons';
import { environment } from 'src/environments/environment';
import { Pilot, User, Vehicle, FlightSession, MultipleApiResponse } from '@mocca-technologies/model';
// Services
import { DroneOperatorService } from 'src/app/services/drone-operator.service';
import { RefreshService } from 'src/app/services/refresh.service';
import { FilesService } from 'src/app/services/files.service';
import { HttpResponsesService } from 'src/app/services/httpResponses.service';
import { catchError, firstValueFrom, forkJoin, map, of, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-pilot-info-card',
  templateUrl: './pilot-info-card.component.html',
  styleUrls: ['./pilot-info-card.component.scss'],
})
export class PilotInfoCardComponent implements OnInit, OnDestroy, OnChanges {
  /**
   * Variables
   */
  user: User;
  droneIcon = droneIcon;
  droneMoccaIcon = droneMoccaIcon;
  vehicleList: Vehicle[] = [];
  expandedCertificates: number[] = [];

  /**
   * Input variables
   */
  @Input() pilot: Pilot;
  @Input() isSummary: boolean = false; // summary mode hides actions and vehicles list

  /**
   * Output variables
   */
  @Output() sessionsUpdated: EventEmitter<void> = new EventEmitter<void>();

  private destroy$ = new Subject<void>();

  /**
   * Avoid overlapping loadVehicles calls (prevents stacked loaders / race conditions)
   */
  private opChain: Promise<void> = Promise.resolve();

  // #region --> Getters / UI helpers

  /** True if current user is admin or developer */
  get isAdminDevel(): boolean {
    return this.user?.profile === 'admin' || this.user?.profile === 'devel';
  }

  /** Avatar exists and is not empty */
  get hasAvatar(): boolean {
    return !!this.pilot?.avatarUrl;
  }

  /** Only the pilot can change their avatar */
  get canEditAvatar(): boolean {
    return this.user?.uniqueId === this.pilot?.uniqueId;
  }

  /**
   * Returns the certificate icon path based on its name.
   */
  getCertificateSource(certificateName: string): string {
    if (!certificateName) return '';

    if (certificateName.includes('A1') && certificateName.includes('A3'))
      return 'assets/icon/A1-A3.png';
    if (certificateName.includes('A2')) return 'assets/icon/A2.png';

    if (
      certificateName.includes('STS') &&
      certificateName.includes('ES') &&
      certificateName.includes('1')
    )
      return 'assets/icon/STS-ES01.png';

    if (
      certificateName.includes('STS') &&
      certificateName.includes('ES') &&
      certificateName.includes('2')
    )
      return 'assets/icon/STS-ES02.png';

    if (certificateName.includes('STS') && certificateName.includes('1'))
      return 'assets/icon/STS-01.png';

    if (certificateName.includes('STS') && certificateName.includes('2'))
      return 'assets/icon/STS-02.png';

    return '';
  }

  /**
   * Whether a certificate card is expanded.
   */
  certificateExpanded(index: number): boolean {
    return this.expandedCertificates.includes(index);
  }

  // #endregion

  constructor(
    private loadingController: LoadingController,
    private cdr: ChangeDetectorRef,
    private droneOperatorService: DroneOperatorService,
    private alertController: AlertController,
    private router: Router,
    private refreshService: RefreshService,
    private filesService: FilesService,
    private httpResponseService: HttpResponsesService,
    private translateService: TranslateService,
  ) {}

  // #region --> Lifecycle

  ngOnInit(): void {
    this.user = localStorage.getItem('user')
      ? JSON.parse(localStorage.getItem('user')!)
      : null;

    !environment.production &&
      console.log('PilotInfoCardComponent initialized with pilot:', this.pilot);

    // In case pilot is already present on init
    if (this.pilot?.uniqueId && !this.isSummary && this.isAdminDevel) {
      this.loadVehicles();
    }
  }

  /**
   * IMPORTANT: when the selected pilot changes, reload associated vehicles ONCE.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pilot']) {
      const newPilot: Pilot = changes['pilot'].currentValue;

      // If pilot is cleared, clear list too
      if (!newPilot?.uniqueId) {
        this.vehicleList = [];
        this.cdr.detectChanges();
        return;
      }

      // Only admin/devel + not summary should load sessions
      if (!this.isSummary && this.isAdminDevel) {
        this.loadVehicles();
      } else {
        // Restricted users shouldn't show this panel anyway, but keep state consistent
        this.vehicleList = [];
        this.cdr.detectChanges();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // #endregion

  // #region --> UI actions

  /**
   * Toggles the expanded state of a certificate entry.
   */
  toggleCertificate(index: number): void {
    this.expandedCertificates = this.expandedCertificates.includes(index)
      ? this.expandedCertificates.filter((i) => i !== index)
      : [...this.expandedCertificates, index];

    this.cdr.detectChanges();
  }

  certificateExpiring(expirationDate: string): number {
    return this.getExpiryUrgency(expirationDate);
  }

  certificatesExpiring(): number {
    if (!this.pilot?.certificateList?.length) return 0;

    let maxUrgency = 0;
    for (const c of this.pilot.certificateList) {
      maxUrgency = Math.max(maxUrgency, this.getExpiryUrgency(c?.expirationDate));
    }
    return maxUrgency;
  }

  async deleteSession(vehicle: Vehicle): Promise<void> {
    const confirmAlert = await this.alertController.create({
      header: this.translateService.instant('PAGES.FLEET.DELETE_SESSION_HEADER'),
      message: this.translateService.instant('PAGES.FLEET.DELETE_SESSION_MESSAGE', {
        vehicle: vehicle?.plate,
        pilot: this.pilot?.contactInformation?.name || this.pilot?.uniqueId,
      }),
      buttons: [
        {
          text: this.translateService.instant('COMMON.BUTTONS.CANCEL'),
          role: 'cancel',
          cssClass: 'secondary',
        },
        {
          text: this.translateService.instant('COMMON.BUTTONS.CONFIRM'),
          handler: () => {
            this.droneOperatorService
              .deleteSession(this.pilot.uniqueId, vehicle?.uniqueId)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: () => {
                  this.loadVehicles();
                  this.sessionsUpdated.emit();
                },
                error: (error: HttpErrorResponse) => {
                  this.httpResponseService.showServerError(error?.status, error?.error);
                },
              });
          },
        },
      ],
    });

    await confirmAlert.present();
  }

  goToPilotForm(): void {
    this.router.navigate(['/pilot-form'], {
      state: {
        pilot: this.pilot,
        isEdit: true,
        organizationType: this.user?.organizationType,
        operatorId: this.pilot?.organizationUuid || this.user?.organizationUuid,
      },
    });
  }

  async deletePilot(pilot: Pilot): Promise<void> {
    const alert = await this.alertController.create({
      header: this.translateService.instant('PAGES.FLEET.DELETE_PILOT_HEADER'),
      message: this.translateService.instant('PAGES.FLEET.DELETE_PILOT_MESSAGE', {
        username: pilot?.username,
      }),
      buttons: [
        { text: this.translateService.instant('COMMON.BUTTONS.CANCEL'), role: 'cancel' },
        {
          text: this.translateService.instant('COMMON.BUTTONS.YES'),
          handler: () => {
            this.droneOperatorService
              .deletePilot(pilot.uniqueId)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: () => this.refreshService.triggerRefresh('fleet'),
                error: (error: HttpErrorResponse) =>
                  this.httpResponseService.showServerError(error?.status, error?.error),
              });
          },
        },
      ],
    });

    await alert.present();
  }

  onAvatarImgError(): void {
    if (this.pilot) this.pilot.avatarUrl = null;
    this.cdr.detectChanges();
  }

  async changeAvatar(): Promise<void> {
    if (!this.hasAvatar) {
      this.selectNewImage();
      return;
    }

    const dialog = await this.alertController.create({
      header: this.translateService.instant('PAGES.FLEET.AVATAR_OPTIONS_HEADER'),
      message: this.translateService.instant('PAGES.FLEET.AVATAR_OPTIONS_MESSAGE'),
      buttons: [
        { text: this.translateService.instant('PAGES.FLEET.CHANGE_IMAGE'), handler: () => this.selectNewImage() },
        { text: this.translateService.instant('PAGES.FLEET.DELETE_IMAGE'), role: 'destructive', handler: () => this.deleteAvatar() },
        { text: this.translateService.instant('COMMON.BUTTONS.CANCEL'), role: 'cancel' },
      ],
    });

    await dialog.present();
  }

  private deleteAvatar(): void {
    this.filesService
      .deletePilotAvatar(this.pilot.uniqueId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.pilot.avatarUrl = null;
          this.cdr.detectChanges();
        },
        error: (error: HttpErrorResponse) =>
          this.httpResponseService.showServerError(error?.status, error?.error),
      });
  }

  selectNewImage(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.style.display = 'none';

    const previousUrl = this.pilot?.avatarUrl || null;

    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        this.droneOperatorService.showServerError('400', 'Please select a valid image file.');
        return;
      }

      const maxSizeInMB = 5;
      if (file.size > maxSizeInMB * 1024 * 1024) {
        this.droneOperatorService.showServerError('400', `Image size must be less than ${maxSizeInMB}MB.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        this.pilot.avatarUrl = reader.result as string;
        this.cdr.detectChanges();

        this.filesService
          .uploadPilotAvatar(file, file.name, this.pilot)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              if (previousUrl && !previousUrl.startsWith('data:')) {
                const clean = previousUrl.split('?')[0];
                const candidate = `${clean}?t=${Date.now()}`;
                this.swapAvatarIfLoads(candidate);
              }
            },
            error: (error: HttpErrorResponse) => {
              this.httpResponseService.showServerError(error?.status, error?.error);
            },
          });
      };

      reader.readAsDataURL(file);
    };

    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  }

  private swapAvatarIfLoads(url: string): void {
    const img = new Image();
    img.onload = () => {
      this.pilot.avatarUrl = url;
      this.cdr.detectChanges();
    };
    img.onerror = () => {};
    img.src = url;
  }

  // #endregion

  // #region --> Loading data

  /**
   * Loads vehicles associated to the current pilot.
   * Uses a queue to avoid duplicated loaders and production race issues.
   */
  async loadVehicles(): Promise<void> {
    return this.enqueue(async () => {
      const loading = await this.loadingController.create({
        message: this.translateService.instant('COMMON.LOADING.VEHICLES'),
        spinner: 'bubbles',
      });

      await loading.present();

      try {
        this.vehicleList = [];

        // If pilot changed to null while we were queued
        if (!this.pilot?.uniqueId) return;

        if (!this.isAdminDevel || this.isSummary) return;

        const sessionsRes = await firstValueFrom(
          this.droneOperatorService
            .getUserSessions(this.pilot.uniqueId)
            .pipe(takeUntil(this.destroy$)),
        );

        const sessions = (sessionsRes?.body?.responseObject || []) as FlightSession[];

        const uuids = Array.from(
          new Set((sessions || []).map((s) => s?.vehicleUuid).filter(Boolean)),
        );

        if (uuids.length === 0) {
          this.vehicleList = [];
          this.cdr.detectChanges();
          return;
        }

        const requests = uuids.map((uuid) =>
          this.droneOperatorService
            .getVehicle(uuid, this.pilot.organizationUuid)
            .pipe(
              takeUntil(this.destroy$),
              map((r: HttpResponse<MultipleApiResponse>) => r?.body?.responseObject as Vehicle),
              catchError(() => of(null)),
            ),
        );

        const vehicles = await firstValueFrom(
          forkJoin(requests).pipe(
            map((list) =>
              (list || [])
                .filter((v) => !!v && !!v.uniqueId)
                .filter((v, i, arr) => arr.findIndex((x) => x.uniqueId === v.uniqueId) === i),
            ),
          ),
        );

        this.vehicleList = vehicles || [];
        this.cdr.detectChanges();
      } catch (error: any) {
        this.httpResponseService.showServerError(error?.status, error?.error);
      } finally {
        await this.safeDismissLoading(loading);
      }
    });
  }

  // #endregion

  // #region --> Drag & drop

  async onVehicleDropped(event: CdkDragDrop<Vehicle[]>): Promise<void> {
    if (event.previousContainer === event.container) return;

    const sourceVehicle = event.item?.data as Vehicle;
    if (!sourceVehicle) return;

    const alreadyExists = this.vehicleList.some((v) => v.uniqueId === sourceVehicle.uniqueId);
    if (alreadyExists) {
      const alert = await this.alertController.create({
        header: this.translateService.instant('PAGES.FLEET.DUPLICATE_VEHICLE_HEADER'),
        message: this.translateService.instant('PAGES.FLEET.DUPLICATE_VEHICLE_MESSAGE'),
        buttons: [this.translateService.instant('COMMON.BUTTONS.OK')],
      });
      await alert.present();
      return;
    }

    const vehicleName = sourceVehicle.plate || sourceVehicle.uniqueId;

    const confirmAlert = await this.alertController.create({
      header: this.translateService.instant('PAGES.FLEET.CONFIRM_SESSION_HEADER'),
      message: this.translateService.instant('PAGES.FLEET.CONFIRM_SESSION_MESSAGE', {
        vehicle: vehicleName,
        pilot: this.pilot?.contactInformation?.name || this.pilot?.uniqueId,
      }),
      buttons: [
        { text: this.translateService.instant('COMMON.BUTTONS.CANCEL'), role: 'cancel', cssClass: 'secondary' },
        {
          text: this.translateService.instant('COMMON.BUTTONS.CONFIRM'),
          handler: () => {
            const vehicleCopy = { ...sourceVehicle };

            this.droneOperatorService
              .createSession(this.pilot.uniqueId, vehicleCopy.uniqueId)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (response: HttpResponse<MultipleApiResponse>) => {
                  if (response?.status === 200) {
                    this.loadVehicles();
                    this.sessionsUpdated.emit();
                  }
                },
                error: (error: HttpErrorResponse) => {
                  this.httpResponseService.showServerError(error?.status, error?.error);
                },
              });
          },
        },
      ],
    });

    await confirmAlert.present();
  }

  // #endregion

  // #region --> TrackBy helpers

  trackByVehicle(_index: number, v: Vehicle): string {
    return v?.uniqueId;
  }

  trackByCertificate(_index: number, c: any): string {
    return `${c?.certificateName || ''}-${c?.licenseNumber || ''}-${_index}`;
  }

  // #endregion

  // #region --> Private helpers

  private enqueue(op: () => Promise<void>): Promise<void> {
    this.opChain = this.opChain
      .then(async () => {
        if (this.destroy$.closed) return;
        await op();
      })
      .catch(() => {});
    return this.opChain;
  }

  private getExpiryUrgency(expirationDate: string): number {
    if (!expirationDate) return 0;

    const [day, month, year] = expirationDate.split('/').map((num) => parseInt(num, 10));
    const expiryDate = new Date(year, month - 1, day);

    const today = new Date();
    const diffTime = expiryDate.getTime() - today.getTime();
    const diffDays = diffTime / (1000 * 3600 * 24);
    const diffMonths = diffDays / 30.44;

    if (diffMonths <= 3) return 2;
    if (diffMonths <= 6) return 1;
    return 0;
  }

  private async safeDismissLoading(loading?: HTMLIonLoadingElement): Promise<void> {
    if (!loading) return;
    try {
      await loading.dismiss();
    } catch {
      // ignore
    }
  }

  // #endregion
}
